import React from 'react';
import { 
    Box,
    Grid,
    styled,
    Typography,
    Container,
    Card,
    CardContent,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import EngineeringIcon from '@mui/icons-material/Engineering';
import imgDetail from '../assets/crane1.jpg';
import imgDetail2 from '../assets/crane2.jpg';

const GetStarted = () => {
    const Section = styled(Box)(({ theme }) => ({
        padding: theme.spacing(10, 0),
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        position: 'relative',
        overflow: 'hidden',
    }));

    const SectionTitle = styled(Typography)(({ theme }) => ({
        fontFamily: "'Outfit', 'Roboto', sans-serif",
        fontWeight: 800,
        fontSize: 'clamp(2rem, 4vw, 3rem)',
        textAlign: 'center',
        marginBottom: theme.spacing(2),
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
    }));

    const SectionSubtitle = styled(Typography)(({ theme }) => ({
        textAlign: 'center',
        color: '#64748b',
        fontSize: '1.1rem',
        marginBottom: theme.spacing(8),
        maxWidth: '700px',
        marginLeft: 'auto',
        marginRight: 'auto',
    }));

    const FeatureCard = styled(Card)(({ theme }) => ({
        height: '100%',
        borderRadius: '20px',
        border: 'none',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #FF6B35 0%, #FF8C42 100%)',
            transform: 'scaleX(0)',
            transition: 'transform 0.4s ease',
        },
        '&:hover': {
            transform: 'translateY(-10px)',
            boxShadow: '0 20px 60px rgba(255, 107, 53, 0.2)',
            '&::before': {
                transform: 'scaleX(1)',
            },
        },
    }));

    const IconWrapper = styled(Box)(({ theme }) => ({
        width: '70px',
        height: '70px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(3),
        boxShadow: '0 10px 30px rgba(255, 107, 53, 0.3)',
        '& svg': {
            fontSize: '2rem',
            color: '#fff',
        },
    }));

    const FeatureTitle = styled(Typography)(({ theme }) => ({
        fontFamily: "'Outfit', 'Roboto', sans-serif",
        fontWeight: 700,
        fontSize: '1.5rem',
        color: '#1e293b',
        marginBottom: theme.spacing(2),
    }));

    const FeatureDescription = styled(Typography)({
        fontSize: '1rem',
        color: '#64748b',
        lineHeight: 1.8,
    });

    const ImageBox = styled(Box)(({ theme }) => ({
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.4s ease',
        height: '100%',
        minHeight: '400px',
        position: 'relative',
        '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 25px 70px rgba(0, 0, 0, 0.2)',
        },
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, transparent 100%)',
            pointerEvents: 'none',
        },
    }));

    return (
        <Section>
            <Container maxWidth="lg">
                <SectionTitle variant="h2">
                    Why Choose Perumal Crane Services?
                </SectionTitle>
                <SectionSubtitle>
                    We deliver excellence in every lift with our state-of-the-art equipment and expert team
                </SectionSubtitle>

                <Grid container spacing={4} sx={{ mb: 8 }}>
                    <Grid item xs={12} md={6}>
                        <FeatureCard>
                            <CardContent sx={{ p: 4 }}>
                                <IconWrapper>
                                    <EngineeringIcon />
                                </IconWrapper>
                                <FeatureTitle>
                                    Experienced Operators
                                </FeatureTitle>
                                <FeatureDescription>
                                    Our team of highly trained operators brings years of experience to every project. 
                                    You can trust us to handle your lifting needs with skill and professionalism, 
                                    ensuring the safety of both personnel and materials throughout the operation.
                                </FeatureDescription>
                            </CardContent>
                        </FeatureCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ImageBox>
                            <img 
                                src={imgDetail} 
                                alt="Professional crane operators at work in Coimbatore" 
                                loading="lazy"
                            />
                        </ImageBox>
                    </Grid>

                    <Grid item xs={12} md={6} sx={{ order: { xs: 4, md: 3 } }}>
                        <ImageBox>
                            <img 
                                src={imgDetail2} 
                                alt="Modern crane equipment for heavy lifting projects" 
                                loading="lazy"
                            />
                        </ImageBox>
                    </Grid>

                    <Grid item xs={12} md={6} sx={{ order: { xs: 3, md: 4 } }}>
                        <FeatureCard>
                            <CardContent sx={{ p: 4 }}>
                                <IconWrapper>
                                    <VerifiedIcon />
                                </IconWrapper>
                                <FeatureTitle>
                                    Customized Solutions
                                </FeatureTitle>
                                <FeatureDescription>
                                    Every project is unique, and we work closely with you to understand your requirements. 
                                    Our team provides personalized solutions, including site assessments and lift planning, 
                                    to ensure a seamless and successful operation every time.
                                </FeatureDescription>
                            </CardContent>
                        </FeatureCard>
                    </Grid>
                </Grid>
            </Container>
        </Section>
    );
};

export default GetStarted;